"use client";

import React, { Suspense, useEffect, useState } from "react";
import {
  RectangleStackIcon,
  CommandLineIcon,
  XMarkIcon,
  Bars3Icon,
} from "@heroicons/react/24/outline";
import Image from "next/image";
import Link from "next/link";
import { DialogSignin } from "@/components/DialogSignin";
import { AvatarWithUserDropdown } from "./AvatarWithUserDropdown";
import profileService from "@/features/profile/service";
import { usePathname, useSearchParams } from "next/navigation";
import { TUser } from "@/features/profile/types";
import { useIsLoggedIn } from "@/features/auth/services";
import CopyButton from "@/components/CopyButton";
import recommenderService, {
  useRecommenderState,
} from "@/features/Recommender/services";

const NAV_MENU = [
  {
    name: "หน้าแรก",
    icon: RectangleStackIcon,
    href: "/",
  },
  {
    name: "ประกันชีวิต",
    icon: CommandLineIcon,
    href: "/insurance",
  },
  {
    name: "แผนรายได้",
    icon: CommandLineIcon,
    href: "/income-plan",
  },
  {
    name: "แผนประกันภัย",
    icon: CommandLineIcon,
    href: "/insurance-plan",
  },
  {
    name: "บทความ",
    icon: CommandLineIcon,
    href: "/blogs",
  },
  {
    name: "สิทธิพิเศษ",
    icon: CommandLineIcon,
    href: "/privilege",
  },
];

function NavbarUpdater() {
  const pathname = usePathname();
  const [openDialogSignin, setOpenDialogSignin] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<TUser | null>(null);
  const isLoggedIn = useIsLoggedIn();
  const recommender = useRecommenderState.getState();
  const searchParams = useSearchParams();

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  useEffect(() => {
    const getUser = async () => {
      try {
        setIsLoading(true);
        const [user] = await Promise.all([
          profileService.getUser(),
          getRecommender(),
        ]);
        setUser(user);
      } catch (error) {
      } finally {
        setIsLoading(false);
      }
    };
    getUser();
  }, [usePathname(), isLoggedIn]);

  const getRecommender = async () => {
    try {
      setIsLoading(true);

      const params = new URLSearchParams(searchParams.toString());
      let id = Number.isNaN(Number(params.get("u")))
        ? recommenderService.getId()
        : Number(params.get("u"));
      id = id ?? 1;
      id = id === 0 ? recommenderService.getId() : id;
      await recommenderService.getMemberById(id);
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <DialogSignin isOpen={openDialogSignin} onClose={setOpenDialogSignin} />
      <nav className="fixed top-0 left-0 right-0 bg-white shadow-md z-50">
        {/* Top section with logo and contact buttons */}
        <div className="flex justify-between items-center h-16 px-4 mx-auto container">
          {/* Logo */}
          <div className="flex-shrink-0 flex items-center">
            <Link
              href="/"
              className="flex items-center gap-4"
              aria-label="หน้าแรก Agent Srikrung"
            >
              <Image
                src="/favicon.png"
                alt="โลโก้ศรีกรุงโบรคเกอร์"
                width={56}
                height={56}
                className="h-14 w-auto"
              />
              <h1 className="text-[#126d37] font-bold text-lg md:text-xl py-4">
                Agent <br /> Srikrung
              </h1>
            </Link>
          </div>

          {/* Contact buttons - visible on both mobile and desktop */}
          <div className="flex items-center space-x-4">
            <CopyButton />
            <Link
              href={recommender?.line ?? "#"}
              target={recommender?.line ? "_blank" : undefined}
              rel="noopener noreferrer"
              className="flex items-center text-[#126d37] hover:text-[#005825] transition focus:outline-none focus:ring-2 focus:ring-[#126d37]/50 rounded-full p-1"
              aria-label="ติดต่อทาง Line"
            >
              <Image
                src="/icons/line.png"
                alt="Line"
                width={42}
                height={42}
                className="mr-1"
              />
            </Link>
            <Link
              href={
                recommender?.phoneNumber
                  ? `tel:${recommender.phoneNumber}`
                  : "#"
              }
              className="flex items-center text-[#126d37] hover:text-[#005825] transition focus:outline-none focus:ring-2 focus:ring-[#126d37]/50 rounded-full p-1"
              aria-label="โทรศัพท์ติดต่อ"
            >
              <Image
                src="/icons/phone.png"
                alt="Phone"
                width={42}
                height={42}
                className="mr-1"
              />
            </Link>
            {isLoading ? (
              <></>
            ) : user && isLoggedIn ? (
              <AvatarWithUserDropdown user={user} />
            ) : (
              <div className="hidden md:block">
                <Link
                  href="/member-register"
                  className="me-3 h-[50px] w-[100px] font-lg px-4 py-2 bg-[#126d37] text-white rounded-full font-bold hover:bg-[#005825] transition"
                  aria-label="สมัครเป็นสมาชิกนายหน้าประกัน"
                  data-name="register-navbar"
                >
                  สมัครสมาชิก
                </Link>
                <button
                  name="signin-navbar"
                  onClick={() => {
                    setOpenDialogSignin(true);
                    setIsMenuOpen(false);
                  }}
                  className="h-[40px] w-[100px] font-lg px-4 py-2 bg-[#126d37] text-white rounded-full font-bold hover:bg-[#005825] transition focus:outline-none focus:ring-4 focus:ring-[#126d37]/50"
                  aria-label="เข้าสู่ระบบสำหรับสมาชิก"
                >
                  เข้าสู่ระบบ
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Navigation section - moved outside container for full width */}
        <div className="flex justify-start md:justify-center items-center gap-8 h-[60px] border-gray-200 bg-[#188F46] w-full px-4">
          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-8">
            {NAV_MENU.map(({ href, name }) => (
              <Link
                key={name}
                className={`text-white font-semibold text-center px-4 py-1 rounded-xl transition-all duration-200
               ${
                 pathname === href
                   ? "bg-[#0d4d29] shadow-md scale-105"
                   : "hover:bg-[#0d4d29] hover:shadow-sm"
               }`}
                href={href}
              >
                {name}
              </Link>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <button
              name="mobile-menu-toggle"
              onClick={toggleMenu}
              className="text-white hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/50 rounded-md p-2 font-semibold"
              aria-label={isMenuOpen ? "ปิดเมนู" : "เปิดเมนู"}
              aria-expanded={isMenuOpen}
            >
              {isMenuOpen ? (
                <div className="flex flex-row gap-1 bg-white w-[80px] h-[35px] rounded-sm justify-center items-center">
                  <XMarkIcon className="h-6 w-6" color="#126d37" />
                  <h3 className="font-lg font-bold text-[#126d37]">ปิด</h3>
                </div>
              ) : (
                <div className="flex flex-row gap-1 bg-white w-[80px] h-[35px] rounded-sm justify-center items-center">
                  <Bars3Icon className="h-6 w-6" color="#126d37" />
                  <h3 className="font-lg font-bold text-[#126d37]">เปิด</h3>
                </div>
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div
            className="md:hidden bg-white border-t"
            role="navigation"
            aria-label="เมนูมือถือ"
          >
            <div className="px-2 pt-2 pb-3 space-y-1">
              {NAV_MENU.map(({ href, name }) => (
                <Link
                  className={`block px-3 py-2 rounded-md font-semibold transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#126d37]/50
                  ${
                    pathname === href
                      ? "text-[#0d4d29] bg-gray-100 underline underline-offset-4"
                      : "text-gray-900 hover:text-[#0d4d29] hover:bg-gray-50"
                  }`}
                  href={href}
                  key={name}
                >
                  {name}
                </Link>
              ))}
              <div className="px-3 py-2 space-y-2">
                <a
                  data-name="register-mobile"
                  href="/member-register"
                  className="block w-full px-4 py-2 bg-[#126d37] text-white rounded-full font-bold hover:bg-[#005825] transition text-center focus:outline-none focus:ring-4 focus:ring-[#126d37]/50"
                  aria-label="สมัครเป็นสมาชิกนายหน้าประกัน"
                >
                  สมัครสมาชิก
                </a>
                {!user && !isLoggedIn && (
                  <button
                    name="signin-mobile"
                    onClick={() => {
                      setOpenDialogSignin(true);
                      setIsMenuOpen(false);
                    }}
                    className="w-full px-4 py-2 border-2 border-[#126d37] text-white bg-[#126d37] rounded-full font-bold hover:bg-[#005825] transition focus:outline-none focus:ring-4 focus:ring-[#126d37]/50"
                    aria-label="เข้าสู่ระบบสำหรับสมาชิก"
                  >
                    เข้าสู่ระบบ
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </nav>
    </>
  );
}

export function Navbar() {
  return (
    <Suspense fallback={null}>
      <NavbarUpdater />
    </Suspense>
  );
}
export default Navbar;
